import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type KeptSignature, keepable, type Signature, type SignatureInput, type SignedSubject } from '@tnn/domain'
import { api } from '../api/client'
import type { NewSignature } from './capture'

/** My kept signature and the signatures on documents, as the API serves them (modules/signature) */

export const mySignatureKey = ['me', 'signature'] as const

/** Uploads a signature just made → its file id */
export async function uploadSignature(signature: NewSignature, path?: string): Promise<string> {
  const stored = await api.upload(new File([signature.blob], 'signature.png', { type: 'image/png' }), path)
  return stored.id
}

/** My kept signature, or null */
export function useMySignature() {
  return useQuery({
    queryKey: mySignatureKey,
    queryFn: () => api.get<{ item: KeptSignature | null }>('/me/signature').then((r) => r.item),
    staleTime: 300_000,
  })
}

/** Keeping a signature as mine: one just made, or one already uploaded (`fileId`) */
export function useKeepSignature() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (signature: NewSignature | { fileId: string; method: NewSignature['method'] }) => {
      const fileId = 'blob' in signature ? await uploadSignature(signature) : signature.fileId
      return api.put<{ item: KeptSignature }>('/me/signature', { fileId, method: signature.method }).then((r) => r.item)
    },
    onSuccess: (item) => client.setQueryData(mySignatureKey, item),
  })
}

export function useForgetSignature() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/me/signature'),
    onSuccess: () => client.setQueryData(mySignatureKey, null),
  })
}

/**
 * How one signs on a form: one's kept signature, one made there and then, or one made on one's portable screen and
 * uploaded from there (a task, `useHandoff`) — the last two kept as one's own too, if asked
 */
export type SignatureChoiceValue =
  | { kind: 'kept' }
  | { kind: 'new'; signature: NewSignature; keep: boolean }
  | { kind: 'uploaded'; fileId: string; method: NewSignature['method']; keep: boolean }

/** What the API takes for `value` (uploading a new one first, and keeping it as mine when asked) */
export async function signatureInput(
  value: SignatureChoiceValue,
  keep?: (fileId: string, method: NewSignature['method']) => Promise<unknown>,
) {
  if (value.kind === 'kept') return { stored: true } as const satisfies SignatureInput
  const method = value.kind === 'new' ? value.signature.method : value.method
  const fileId = value.kind === 'new' ? await uploadSignature(value.signature) : value.fileId
  // Only a photographed one is kept as mine (`keepable`); one drawn there and then signs this alone
  if (value.keep && keep && keepable(method)) await keep(fileId, method)
  return { fileId, method } satisfies SignatureInput
}

/** The signatures on one document, oldest first */
export function useSignatures(subject: SignedSubject, recordId: string | undefined) {
  return useQuery({
    queryKey: ['signatures', subject, recordId],
    queryFn: () => api.get<{ items: Signature[] }>(`/signatures/${subject}/${recordId}`).then((r) => r.items),
    enabled: !!recordId,
  })
}
